import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Modal, Stack, Text, Textarea } from '@mantine/core';
import { IconFiles, IconId, IconLicense } from '@tabler/icons-react';
import {
  RIDES_PROVIDERS,
  RIDES_VERIFICATION_TYPE,
  useRidesSecurityService,
  type RidesSecurityRiderView,
  type RidesSecurityRowView,
} from '@actschurch/ccms-sdk';
import { ridesKeys } from '@/lib/queryKeys';
import { errorMessage } from '@/lib/copy';
import { formatStamp, formatTime, plural } from '@/lib/format';
import { useCopy, visiblePhoto, type PhotoRules } from '@/lib/settings';
import { Callout } from '@/components/ui';
import { PersonAvatar } from '@/components/rides/PersonAvatar';
import { PlateChip } from '@/components/rides/PlateChip';
import { Pill } from '@/components/rides/Pill';
import { EmergencyContactLine } from '@/components/rides/EmergencyContactLine';
import { SecurityDocumentsModal } from '@/components/rides/SecurityDocumentsModal';
import { carWords } from '@/components/member/trips';
import { Disclosure } from '@/components/member/Disclosure';
import { CheckAction } from './CheckAction';
import '@/styles/member.css';

/** Lookup names → what a guard would say ("SouthAfricanId" → "SA ID"). */
export function idDocumentWords(name: string | null | undefined): string {
  const n = (name ?? '').replace(/\s+/g, '').toLowerCase();
  if (!n) return 'ID';
  if (n.includes('southafrican') || n === 'said' || n === 'idcard' || n === 'idbook') return 'SA ID';
  if (n.includes('passport')) return 'Passport';
  return name ?? 'ID';
}

export function isChecked(row: RidesSecurityRowView): boolean {
  return row.direction === 'pickup' ? !!row.pickupCheckedAt : !!row.dropOffCheckedAt;
}

function RiderLine({ rider, photos }: { rider: RidesSecurityRiderView; photos: PhotoRules }) {
  const child = rider.forMinor;
  // A child's booking is made from the guardian's account, so that photo isn't the child.
  const photo = visiblePhoto(rider.riderPhotoUrl, photos.security && (!child || photos.minors));
  const childName = rider.childName?.trim();
  const name = child ? childName || "Child's name not given" : rider.riderName;
  const sub = child
    ? `Under 18. Booked by ${rider.guardianName?.trim() || rider.riderName}.`
    : rider.partySize > 1
      ? `${plural(rider.partySize, 'person', 'people')} on this booking`
      : null;
  const hasContact = !!(rider.emergencyContact?.name || rider.emergencyContact?.phone);
  return (
    <div className="rm-rider">
      {photos.enabled && <PersonAvatar src={photo} name={child ? childName || 'Child' : name} size="md" decorative />}
      <div className="txt">
        <b>{child && <span className="childtag">Child</span>}{name}</b>
        {sub && <span className="sub">{sub}</span>}
        <Pill kind={rider.idVerified ? 'ok' : 'warn'}>
          {child ? (rider.idVerified ? "Guardian's ID checked" : "Guardian's ID not checked") : rider.idVerified ? 'ID checked' : 'ID not checked'}
        </Pill>
        {hasContact && (
          <div className="rm-small" style={{ marginTop: 4, color: 'var(--rides-ink)' }}>
            <span style={{ color: 'var(--rides-muted)', display: 'block', fontSize: 14 }}>Emergency contact</span>
            <EmergencyContactLine contact={rider.emergencyContact} align="start" />
          </div>
        )}
      </div>
    </div>
  );
}

interface Props {
  row: RidesSecurityRowView;
  provider: string;
  photos: PhotoRules;
  onChanged: () => void;
}

/**
 * One expected driver: photo, name, car and number plate first (what a guard compares), then ID
 * and licence status, the trip, riders (children by their own name), emergency contacts and
 * documents when the church allows it, and the big check button.
 */
export function DriverCard({ row, provider, photos, onChanged }: Props) {
  const { t } = useCopy();
  const security = useRidesSecurityService();
  const qc = useQueryClient();
  const [verifyType, setVerifyType] = useState<null | number>(null);
  const [note, setNote] = useState('');
  const [docsOpen, setDocsOpen] = useState(false);
  const documentCount = row.driverDocumentCount ?? 0;
  const pickup = row.direction === 'pickup';
  const checked = isChecked(row);
  const idWord = idDocumentWords(row.idDocumentTypeName);
  const dep = formatTime(row.departureTime);
  const svc = formatTime(row.serviceTimeStart);

  const recordVerification = useMutation({
    mutationFn: (passed: boolean) => security.recordVerification(row.driverUserId, verifyType!, { passed, notes: note.trim() || null }),
    onSuccess: () => { setVerifyType(null); setNote(''); qc.invalidateQueries({ queryKey: ridesKeys.all }); onChanged(); },
  });

  const hasDriverContact = !!(row.driverEmergencyContact?.name || row.driverEmergencyContact?.phone);
  const manual = provider === RIDES_PROVIDERS.Manual;
  const arrivedStamp = formatStamp(row.arrivedAt);
  const arrivedTime = arrivedStamp.split(', ')[1] ?? arrivedStamp;

  const details = (
    <>
      <div className="pills">
        <Pill kind={row.idVerified ? 'ok' : 'warn'}>{row.idVerified ? `${idWord} checked` : `${idWord} not checked yet`}</Pill>
        <Pill kind={row.licenceVerified ? 'ok' : 'warn'}>{row.licenceVerified ? 'Licence checked' : 'Licence not checked yet'}</Pill>
        {row.driverClearedForMinors && <Pill kind="ok">Can drive under-18s</Pill>}
      </div>

      <div className="trip">
        <b>{pickup ? `Picks up here${dep ? ` at ${dep}` : ''}` : `Drops off here${svc ? ` for the ${svc} service` : ''}`}</b>
        <span>
          {pickup
            ? `Going to ${row.dropOffName}${svc ? ` for the ${svc} service` : ''}.`
            : `Coming from ${row.collectionName}${dep ? `, leaving there at ${dep}` : ''}.`}
          {' '}{row.eventName}. {row.seatsBooked} of {plural(row.seatsTotal, 'seat')} booked.
        </span>
        {row.arrivedAt && <span style={{ color: 'var(--rides-ok)', fontWeight: 600 }}>Driver said they arrived at {arrivedTime}.</span>}
      </div>

      {hasDriverContact && (
        <div className="contact">
          <span className="k">Emergency contact</span>
          <EmergencyContactLine contact={row.driverEmergencyContact} align="start" />
        </div>
      )}

      <div className="rm-riders">
        <h3>{row.riders.length === 0 ? 'No riders booked' : `Riders (${row.riders.length})`}</h3>
        {row.riders.map((p) => <RiderLine key={p.rideBookingID} rider={p} photos={photos} />)}
      </div>
    </>
  );

  return (
    <article className={`rides-card rm-dcard${checked ? ' done' : ''}`} aria-labelledby={`rm-driver-${row.rideAvailabilityID}-${row.direction}`}>
      <div className="who">
        <PersonAvatar src={visiblePhoto(row.driverPhotoUrl, photos.security)} name={row.driverName} size="lg" alt={`Photo of ${row.driverName}`} />
        <div className="txt">
          <b id={`rm-driver-${row.rideAvailabilityID}-${row.direction}`}>{row.driverName}</b>
          <span>{carWords(row.vehicle)}</span>
        </div>
      </div>
      <div className="plate"><PlateChip plate={row.vehicle?.plate} large /></div>

      {checked ? (
        <>
          <div className="rm-actions">
            <CheckAction rideAvailabilityID={row.rideAvailabilityID} driverName={row.driverName} pickup={pickup} checkedAt={pickup ? row.pickupCheckedAt : row.dropOffCheckedAt} onSaved={onChanged} />
          </div>
          <div style={{ marginTop: 6 }}>
            <Disclosure label="Show trip and riders">{details}</Disclosure>
          </div>
        </>
      ) : (
        details
      )}

      <div className="rm-actions">
        {!checked && <CheckAction rideAvailabilityID={row.rideAvailabilityID} driverName={row.driverName} pickup={pickup} checkedAt={pickup ? row.pickupCheckedAt : row.dropOffCheckedAt} onSaved={onChanged} />}
        {documentCount > 0 && (
          <Button variant="default" fullWidth leftSection={<IconFiles size={18} />} onClick={() => setDocsOpen(true)}>
            See documents ({documentCount})
          </Button>
        )}
        {manual && (!row.idVerified || !row.licenceVerified) && (
          <div className="rm-btnrow">
            {!row.idVerified && <Button variant="default" leftSection={<IconId size={18} />} onClick={() => setVerifyType(RIDES_VERIFICATION_TYPE.GovernmentId)}>Record ID check</Button>}
            {!row.licenceVerified && <Button variant="default" leftSection={<IconLicense size={18} />} onClick={() => setVerifyType(RIDES_VERIFICATION_TYPE.DriversLicence)}>Record licence check</Button>}
          </div>
        )}
      </div>

      {documentCount > 0 && (
        <SecurityDocumentsModal opened={docsOpen} onClose={() => setDocsOpen(false)} driverUserId={row.driverUserId} driverName={row.driverName} />
      )}

      <Modal
        opened={verifyType != null}
        onClose={() => setVerifyType(null)}
        title={verifyType === RIDES_VERIFICATION_TYPE.DriversLicence ? `Licence check for ${row.driverName}` : `ID check for ${row.driverName}`}
        centered
      >
        <Stack gap="md">
          <Text size="md">{t('security.record_help')}</Text>
          <Textarea label="Note (optional)" description="Please don't write ID numbers or other personal details." value={note} onChange={(e) => setNote(e.currentTarget.value)} autosize minRows={2} maxLength={200} />
          {recordVerification.error && <Callout tone="bad" live>{errorMessage(recordVerification.error)}</Callout>}
          <div className="rm-btnrow">
            <Button color="red" variant="light" loading={recordVerification.isPending} onClick={() => recordVerification.mutate(false)}>Didn't pass</Button>
            <Button color="green" loading={recordVerification.isPending} onClick={() => recordVerification.mutate(true)}>Passed</Button>
          </div>
        </Stack>
      </Modal>
    </article>
  );
}
